/**
 * WritingPathsCard.jsx — Writing Paths progress panel (Phase 2).
 *
 * Renders below the WriterStatsCard on the Scribe Dashboard. Shows every path
 * the scribe has unlocked so far, with progress bars (0–100), focus toggles
 * (max 2), active toggles (max 6), and level-locked placeholders for paths
 * that unlock later (10/25/50/75).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Target, Lock, Star, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GROUP_META = {
  starter:  { label: 'Starter',  unlock: 1,  hex: '#9CA3AF' },
  genre:    { label: 'Genre',    unlock: 10, hex: '#10B981' },
  advanced: { label: 'Advanced', unlock: 25, hex: '#3B82F6' },
  master:   { label: 'Master',   unlock: 50, hex: '#A855F7' },
  elite:    { label: 'Elite',    unlock: 75, hex: '#F59E0B' },
};

const groupFor = (unlockLevel) => {
  if (unlockLevel >= 75) return 'elite';
  if (unlockLevel >= 50) return 'master';
  if (unlockLevel >= 25) return 'advanced';
  if (unlockLevel >= 10) return 'genre';
  return 'starter';
};

const PathRow = ({ path, currentLevel, onToggleFocus, onToggleActive }) => {
  const locked = (path.unlockLevel || 1) > currentLevel;
  const group = groupFor(path.unlockLevel || 1);
  const meta = GROUP_META[group];
  const score = Number(path.score || 0);
  const safeId = path.pathName.replace(/\s+|\//g, '-');

  return (
    <div
      className={`p-3 rounded-lg border transition-colors ${
        locked
          ? 'bg-slate-900/30 border-slate-800 opacity-60'
          : path.focused
          ? 'bg-amber-500/10 border-amber-500/40'
          : path.active
          ? 'bg-slate-800/60 border-slate-700'
          : 'bg-slate-900/40 border-slate-800'
      }`}
      data-testid={`path-row-${safeId}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-gray-400 mb-1">
            <span
              className="px-1.5 py-0.5 rounded-full text-[9px] font-bold"
              style={{ backgroundColor: `${meta.hex}22`, color: meta.hex, border: `1px solid ${meta.hex}55` }}
            >
              {meta.label}
            </span>
            {locked && <span className="inline-flex items-center gap-0.5"><Lock className="w-2.5 h-2.5" /> L{path.unlockLevel}</span>}
          </div>
          <h4 className="text-sm font-semibold text-white truncate">{path.pathName}</h4>
        </div>
        {!locked && (
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={() => onToggleFocus(path)}
              title={path.focused ? 'Remove focus' : 'Focus (1.5× growth)'}
              className={`p-1.5 rounded-md border text-xs transition-colors ${
                path.focused
                  ? 'bg-amber-500 border-amber-400 text-slate-900'
                  : 'bg-slate-800 border-slate-600 text-gray-400 hover:text-white'
              }`}
              data-testid={`path-focus-${safeId}`}
            >
              <Star className={`w-3.5 h-3.5 ${path.focused ? 'fill-current' : ''}`} />
            </button>
            <button
              type="button"
              onClick={() => onToggleActive(path)}
              title={path.active ? 'Pause path' : 'Activate path'}
              className={`p-1.5 rounded-md border text-xs transition-colors ${
                path.active
                  ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-200'
                  : 'bg-slate-800 border-slate-600 text-gray-400 hover:text-white'
              }`}
              data-testid={`path-active-${safeId}`}
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
      <div className="h-1.5 bg-slate-900/60 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${score}%`, backgroundColor: meta.hex }}
          data-testid={`path-score-bar-${safeId}`}
        />
      </div>
      <div className="flex items-center justify-between mt-1 text-[10px] text-gray-500">
        <span>{score.toFixed(1)} / 100</span>
        {score >= 80 && !locked && <span className="text-amber-300">Plateau zone</span>}
      </div>
    </div>
  );
};

const WritingPathsCard = ({ currentLevel = 1 }) => {
  const [paths, setPaths] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      const res = await authFetch(`${BACKEND_URL}/api/writer/stats`);
      const d = await res.json();
      setPaths(d.writingPaths || []);
    } catch { /* noop */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const toggleFocus = async (path) => {
    const focused = paths.filter((p) => p.focused).map((p) => p.pathName);
    const next = path.focused
      ? focused.filter((n) => n !== path.pathName)
      : [...focused, path.pathName];
    if (next.length > 2) {
      toast.info('You can focus at most 2 paths per month. Unfocus one first.');
      return;
    }
    try {
      const res = await authFetch(`${BACKEND_URL}/api/writer/paths/focus`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ focused: next }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      reload();
    } catch (e) {
      toast.error(e.message || 'Could not save focus');
    }
  };

  const toggleActive = async (path) => {
    const active = paths.filter((p) => p.active).map((p) => p.pathName);
    const next = path.active
      ? active.filter((n) => n !== path.pathName)
      : [...active, path.pathName];
    if (next.length > 6) {
      toast.info('You can activate at most 6 paths at a time.');
      return;
    }
    try {
      const res = await authFetch(`${BACKEND_URL}/api/writer/paths/active`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: next }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      reload();
    } catch (e) {
      toast.error(e.message || 'Could not save active paths');
    }
  };

  if (loading) {
    return (
      <Card className="bg-slate-800/50 border-slate-700" data-testid="writing-paths-card">
        <CardContent className="p-6 text-gray-400 text-sm flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading paths…
        </CardContent>
      </Card>
    );
  }

  if (!paths || paths.length === 0) {
    return (
      <Card className="bg-slate-800/50 border-slate-700" data-testid="writing-paths-card">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Target className="w-5 h-5 text-purple-400" /> Writing Paths
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-400">
            Complete the Scribe Assessment to unlock your starter paths.
          </p>
        </CardContent>
      </Card>
    );
  }

  const focusedCount = paths.filter((p) => p.focused).length;
  const activeCount = paths.filter((p) => p.active).length;

  return (
    <Card className="bg-slate-800/50 border-slate-700" data-testid="writing-paths-card">
      <CardHeader className="flex-row items-center justify-between flex-wrap gap-2 space-y-0">
        <CardTitle className="text-white flex items-center gap-2">
          <Target className="w-5 h-5 text-purple-400" /> Writing Paths
        </CardTitle>
        <div className="flex items-center gap-3 text-[11px] text-gray-400">
          <span><span className="text-emerald-300 font-semibold">{activeCount}</span>/6 active</span>
          <span><span className="text-amber-300 font-semibold">{focusedCount}</span>/2 focused</span>
        </div>
      </CardHeader>
      <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {paths
          .slice()
          .sort((a, b) => (a.unlockLevel || 1) - (b.unlockLevel || 1) || a.pathName.localeCompare(b.pathName))
          .map((p) => (
            <PathRow
              key={p.pathName}
              path={p}
              currentLevel={currentLevel}
              onToggleFocus={toggleFocus}
              onToggleActive={toggleActive}
            />
          ))}
      </CardContent>
    </Card>
  );
};

export default WritingPathsCard;
